/* eslint-disable import/no-named-as-default-member */
import React from "react";
import { Grid, Checkbox } from "@material-ui/core";
import _ from "lodash";
import styled from "styled-components";
import { connect as reduxConnect } from "react-redux";
import { connect } from "formik";
import num from "numeral";
import { compose } from "redux";
import loanTypes, { loanTypeList } from "../../../../constants/loanTypes";
import loanStatuses, { loanStatusList } from "../../../../constants/loanStatuses";
import loanSeniorityTypes, { loanSeniorityTypeList } from "../../../../constants/loanSeniorityTypes";
import interestRateTypes, { interestRateTypeList } from "../../../../constants/interestRateTypes";
import { floatingIndexList } from "../../../../constants/floatingIndexes";
import fixedIndexes, { fixedIndexList } from "../../../../constants/fixedIndexes";
import { businessDayAdjustmentList } from "../../../../constants/businessDayAdjustments";
import mezzFundingStructures, { mezzFundingStructureList } from "../../../../constants/mezzFundingStructures";
import { mezzRateTypeList } from "../../../../constants/mezzRateTypes";
import { roundingList } from "../../../../constants/roundings";
import { daycountList } from "../../../../constants/daycounts";
import dealStatuses from "../../../../constants/status";
import AddButton from "../../../Global/Buttons/AddButton";
import { getFilteredLoanStatuses } from "../../../../utils/statusWorkflowHelper";

import {
  ChildSectionTitle,
  SideLabelTextInput,
  SideLabelAutoCompleteInput,
  SideLabelMoneyInput,
  SideLabelCalculatedField,
  SideLabelDateInput,
  SideLabelIntegerInput,
  SideLabelPercentInput,
  SideLabelTextAreaInput,
  SideLabelLenderWithAllocationAndLead
} from "../FormSectionComponents";

const LoanDetailsGrid = styled(Grid)`
  &&& {
    width: 1000px;
  }
`;

const AddLenderButton = styled(AddButton)`
  && {
    margin-left: 220px;
  }
`;

/*
  NOTE: This is only needed to work around a bug in the AutoCompleteInput.  -- Jonathan Byrne 01/23/2020
*/
const SideLabelAutoCompleteInputHack = styled(SideLabelAutoCompleteInput)`
   {
    width: 100%;
  }
`;

const filterLoanTypesByLoanSeniorityType = loanSeniorityId => {
  let ids = [];
  if (loanSeniorityId === loanSeniorityTypes.SENIOR) {
    ids = [1, 2, 3, 4, 5, 6, 7, 8, 10, 11, 13];
  } else if (loanSeniorityId === loanSeniorityTypes.SUBORDINATE) {
    ids = [9, 12];
  } else if (loanSeniorityId === loanSeniorityTypes.SUPPLEMENTAL) {
    ids = [6, 7];
  }
  return _.filter(loanTypeList, v => _.includes(ids, v.value));
};

const seniorLoanTypes = filterLoanTypesByLoanSeniorityType(loanSeniorityTypes.SENIOR);
const subordinateLoanTypes = filterLoanTypesByLoanSeniorityType(loanSeniorityTypes.SUBORDINATE);
const supplementalLoanTypes = filterLoanTypesByLoanSeniorityType(loanSeniorityTypes.SUPPLEMENTAL);

class LoanDetailsChildSection extends React.Component {
  getLoanSeniority = values => {
    return _.get(values, "details.loanSeniorityId");
  };

  isValueAddOrConstructionOrHudOrLineOfCredit = values => {
    const loanTypeId = _.get(values, "details.loanTypeId");
    return (
      loanTypeId === loanTypes.CONSTRUCTION || loanTypeId === loanTypes.VALUE_ADD || loanTypeId === loanTypes.HUD || loanTypeId === loanTypes.LINE_OF_CREDIT
    );
  };

  isSubordinateAndSequential = values => {
    const loanSeniorityId = _.get(values, "details.loanSeniorityId");
    const mezzFundingStructureId = _.get(values, "details.mezzFundingStructureId");
    const loanStatusId = _.get(values, "details.loanStatusId");

    return (
      (this.isMezz(loanSeniorityId) && this.isSequential(mezzFundingStructureId)) && loanStatusId !== loanStatuses.PASSED_QUOTE
    );
  };

  isPaidOff = values => {
    const loanStatusId = _.get(values, "details.loanStatusId");
    return loanStatusId === loanStatuses.PAID_OFF;
  };

  getInterestRateType = values => _.get(values, "details.rateTypeId");

  getIndexType = values => _.get(values, "details.indexId");

  isFixed = interestRateTypeId => interestRateTypes.FIXED === interestRateTypeId;

  isFloating = interestRateTypeId => interestRateTypes.FLOATING === interestRateTypeId;

  isSequential = mezzFundingStructureId => mezzFundingStructures.SEQUENTIAL === mezzFundingStructureId;

  isMezz = loanSeniorityId => {
    return loanSeniorityTypes.SUBORDINATE === loanSeniorityId;
  };

  isInternallyFixed = indexTypeId => indexTypeId === fixedIndexes.INTERNALLY_FIXED;

  getIndexTypes() {
    const { values } = this.props.formik;
    const interestRateTypeId = this.getInterestRateType(values);
    if (values && interestRateTypeId) {
      return this.isFloating(interestRateTypeId) ? floatingIndexList : fixedIndexList;
    }
    return [];
  }

  addEmptyLender = () => {
    const { formik } = this.props;
    const { setFieldValue, values } = formik;
    const { details } = values;

    const emptyLender = {
      id: null,
      name: null,
      loanCompanyBridge: {
        loanId: details.id,
        companyId: null,
        lenderAllocation: null,
        isLead: false
      }
    };
    const loanProviders = [...details.loanProvider, emptyLender];
    setFieldValue("details.loanProvider", loanProviders);
  };

  removeLender = index => {
    const { formik } = this.props;
    const { setFieldValue, values } = formik;
    const { details } = values;

    // Add property to indicatate deleted
    const loanProviders = [...details.loanProvider.slice(0, index), ...details.loanProvider.slice(index + 1)];

    if (loanProviders.length === 1) {
      loanProviders[0].loanCompanyBridge.isLead = true;
      loanProviders[0].loanCompanyBridge.lenderAllocation = details.amount;
    }

    setFieldValue("details.loanProvider", loanProviders);
  };

  hideLenderAllocationAndCheckbox = details => {
    if (!details.loanProvider) return true;
    return details.loanProvider.length < 2;
  };

  showLoanStatus = () => {
    const { formik, isQuote, authentication, guestPermissions } = this.props;
    const { values } = formik;
    const { details } = values;

    if (details.loanStatusId === "") return false;
    if (!isQuote || !authentication.isGuest) return true;
    return authentication.isGuest && guestPermissions.pipelineAllowQuoteStatus;
  };

  render() {
    const { formik, isEditing, ancillary, isQuote, isPassedLoan, deal } = this.props;
    const { values } = formik;
    const { details } = values;
    const loanSeniorityId = this.getLoanSeniority(values);
    const interestRateTypeId = this.getInterestRateType(values);
    const indexTypeId = this.getIndexType(values);
    const isInternallyFixed = this.isInternallyFixed(indexTypeId);

    const isMezz = this.isMezz(loanSeniorityId);
    const isFixed = this.isFixed(interestRateTypeId);
    const isFloating = this.isFloating(interestRateTypeId);
    const lenders = ancillary.lenders;
    const servicerOptions = ancillary.servicers;
    const sortedLendersList = lenders.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });

    if (!details.loanProvider || details.loanProvider.length === 0) {
      this.addEmptyLender();
    }

    if (!details || !deal) return null;

    let filteredLoanSeniorityTypeList;
    if (
      deal.dealStatusId === dealStatuses.ADDING_SUPPLEMENTAL &&
      (!details.loanSeniorityId || details.loanSeniorityId === "" || details.loanSeniorityId === loanSeniorityTypes.SUPPLEMENTAL)
    ) {
      filteredLoanSeniorityTypeList = loanSeniorityTypeList.filter(loanSeniorityType => loanSeniorityType.value === loanSeniorityTypes.SUPPLEMENTAL);
    } else {
      filteredLoanSeniorityTypeList = loanSeniorityTypeList;
    }

    const filteredLoanStatusList = getFilteredLoanStatuses(loanStatusList, details.loanStatusId);

    return (
      <>
        <div id="loan_section_loan_details">
          <ChildSectionTitle>Loan Terms</ChildSectionTitle>
          <LoanDetailsGrid container alignItems="center" direction="column">
            {details.loanProvider && details.loanProvider.length > 0 && (
              <SideLabelLenderWithAllocationAndLead
                label="Lender(s)*"
                lenderAllocation="details.loanProvider[0].loanCompanyBridge.lenderAllocation"
                fieldName="details.loanProvider[0].id"
                options={sortedLendersList}
                placeholder="Lender Allocation"
                selectName="name"
                selectValue="value"
                isDisabled={!isEditing}
                hideLenderAllocationAndLeadCheckbox={this.hideLenderAllocationAndCheckbox(details)}
                hideDeleteButton
                hasLeadSelected={!details.loanProvider || details.loanProvider.some(lender => lender.loanCompanyBridge.isLead)}
                index={0}
                required
              />
            )}
            {details.loanProvider &&
              details.loanProvider.map((_x, i) => {
                if (i === 0) return null;
                return (
                  <SideLabelLenderWithAllocationAndLead
                    key={i}
                    label=""
                    lenderAllocation={`details.loanProvider[${i}].loanCompanyBridge.lenderAllocation`}
                    fieldName={`details.loanProvider[${i}].id`}
                    options={sortedLendersList}
                    placeholder="Lender Allocation"
                    selectName="name"
                    selectValue="value"
                    isDisabled={!isEditing}
                    hideDeleteButton={this.hideLenderAllocationAndCheckbox(details)}
                    hasLeadSelected={!details.loanProvider || details.loanProvider.some(lender => lender.loanCompanyBridge.isLead)}
                    index={i}
                    removeLender={this.removeLender}
                  />
                );
              })}
            {isEditing && (
              <Grid container>
                <AddLenderButton title="ADD LENDER" onClick={this.addEmptyLender} />
              </Grid>
            )}
            {servicerOptions && 
              <SideLabelAutoCompleteInput
                label="Servicer Contact"
                fieldName="details.servicerCompanyId"
                selectValue="servicerValue"
                id="dropdownForServicerContact"
                options={servicerOptions}
                isDisabled={!isEditing}
                shouldHideForQuote={isQuote}
              />}
            <SideLabelAutoCompleteInputHack
              label="Loan Seniority *"
              fieldName="details.loanSeniorityId"
              selectValue="value"
              id="dropdownForLoanSeniority"
              options={filteredLoanSeniorityTypeList}
              isDisabled={!isEditing}
              required
            />
            {this.showLoanStatus() && (
              <SideLabelAutoCompleteInput
                label="Loan Status"
                fieldName="details.loanStatusId"
                id="dropdownForLoanStatus"
                selectValue="value"
                options={filteredLoanStatusList}
                isDisabled={!isEditing}
              />
            )}

            {loanSeniorityId && loanSeniorityId === loanSeniorityTypes.SENIOR && (
              <SideLabelAutoCompleteInput
                label="Loan Type"
                id="dropdownForLoanType"
                fieldName="details.loanTypeId"
                selectValue="value"
                options={seniorLoanTypes}
                isDisabled={!isEditing}
                required
              />
            )}

            {!isPassedLoan && (
              <>
                {loanSeniorityId && loanSeniorityId === loanSeniorityTypes.SUBORDINATE && (
                  <SideLabelAutoCompleteInput
                    label="Loan Type"
                    fieldName="details.loanTypeId"
                    selectValue="value"
                    id="dropdownForLoanType"
                    options={subordinateLoanTypes}
                    isDisabled={!isEditing}
                    required
                  />
                )}

                {loanSeniorityId && loanSeniorityId === loanSeniorityTypes.SUPPLEMENTAL && (
                  <SideLabelAutoCompleteInput
                    label="Loan Type"
                    id="dropdownForLoanType"
                    fieldName="details.loanTypeId"
                    selectValue="value"
                    options={supplementalLoanTypes}
                    isDisabled={!isEditing}
                    required
                  />
                )}

                <SideLabelTextInput
                  label="Loan Reference Number"
                  fieldName="details.loanReferenceNumber"
                  placeholder="Loan Reference Number"
                  isDisabled={!isEditing}
                  shouldHideForQuote={isQuote}
                />
                <SideLabelTextAreaInput
                  label="Borrowing Entity"
                  fieldName="details.counterparty"
                  placeholder="Borrowing Entity"
                  isDisabled={!isEditing}
                  shouldHideForQuote={isQuote}
                />
                <SideLabelCalculatedField
                  label="Current Principal Balance"
                  calculationFunc={fieldValues => num(_.get(fieldValues, "details.currentPrincipalBalance")).format("$0,0")}
                  placeholder="Current Principal Balance"
                  isDisabled={!isEditing}
                  shouldHideForQuote={isQuote}
                />
                <SideLabelCalculatedField
                  label="Current Payment"
                  calculationFunc={fieldValues => num(_.get(fieldValues, "details.currentPayment")).format("$0,0")}
                  placeholder="Current Payment"
                  isDisabled={!isEditing}
                  shouldHideForQuote={isQuote}
                />
                <SideLabelCalculatedField
                  label="Current Prepayment"
                  calculationFunc={fieldValues => num(_.get(fieldValues, "details.prepaymentPenalty")).format("$0,0")}
                  placeholder="Current Prepayment"
                  isDisabled={!isEditing}
                  shouldHideForQuote={isQuote}
                />
                {(this.isValueAddOrConstructionOrHudOrLineOfCredit(values) || this.isSubordinateAndSequential(values)) && (
                  <SideLabelCalculatedField
                    label="Loan Proceeds Available"
                    calculationFunc={fieldValues => {
                      const amount = _.get(fieldValues, "details.amount");
                      const proceedsDrawn = _.get(fieldValues, "details.proceedsDrawn");
                      if (amount && proceedsDrawn) {
                        return num(amount - proceedsDrawn).format("$0,0");
                      }
                      return num(_.get(fieldValues, "details.loanProceedsAvailable")).format("$0,0");
                    }}
                    placeholder="Loan Proceeds Available"
                    isDisabled={!isEditing}
                    shouldHideForQuote={isQuote}
                  />
                )}

                {(this.isValueAddOrConstructionOrHudOrLineOfCredit(values) || this.isSubordinateAndSequential(values)) && (
                  <SideLabelMoneyInput label="Initial Proceeds" fieldName="details.initialProceeds" placeholder="Initial Proceeds" isDisabled={!isEditing} />
                )}
                {(this.isValueAddOrConstructionOrHudOrLineOfCredit(values) || this.isSubordinateAndSequential(values)) && (
                  <SideLabelMoneyInput
                    label="Proceeds Drawn"
                    fieldName="details.proceedsDrawn"
                    placeholder="Proceeds Drawn"
                    isDisabled={!isEditing}
                    shouldHideForQuote={isQuote}
                  />
                )}
                 {(this.isValueAddOrConstructionOrHudOrLineOfCredit(values) || this.isSubordinateAndSequential(values)) && (
                  <SideLabelDateInput
                    label="As Of Date"
                    fieldName="details.proceedsDrawnAsOf"
                    placeholder="MM/DD/YYYY"
                    isDisabled={!isEditing}
                    shouldHideForQuote={isQuote}
                  />
                )}
                <SideLabelMoneyInput label="Loan Amount" fieldName="details.amount" placeholder="Loan Amount" isDisabled={!isEditing} />
                {isQuote && (
                  <SideLabelIntegerInput label="Term (Months)" fieldName="details.loanTermMonths" placeholder="Term (Months)" isDisabled={!isEditing} />
                )}
                <SideLabelCalculatedField
                  label="All-In Rate"
                  calculationFunc={fieldValues => num(fieldValues.details.allInRate / 100).format("0.000%")}
                  placeholder="All-In Rate"
                  isDisabled={!isEditing}
                  decimalScale={3}
                  shouldHideForQuote={isQuote}
                />
                {isMezz && (
                  <SideLabelAutoCompleteInput
                    label="Funding Structure"
                    id="dropdownForFundingStructure"
                    fieldName="details.mezzFundingStructureId"
                    selectValue="value"
                    options={mezzFundingStructureList}
                    isDisabled={!isEditing}
                    shouldHideForQuote={isQuote}
                  />
                )}
                <SideLabelDateInput
                  label="Closing Date"
                  fieldName="details.closingDate"
                  placeholder="MM/DD/YYYY"
                  isDisabled={!isEditing}
                  shouldHideForQuote={isQuote}
                  auxComponent = {
                    <div style={{display: "inline-block", marginLeft: "10px"}}>
                      {(values.details.isClosingDateAssumed || isEditing) &&
                      <div style={{display: 'inline-block', marginTop: "4px"}}>
                      Assumed?
                      <Checkbox
                      disabled = {!isEditing}
                      checked = {values.details.isClosingDateAssumed}
                      onClick = {()=>formik.setFieldValue("details.isClosingDateAssumed", !values.details.isClosingDateAssumed)}
                      />
                      </div>
                      }
                    </div>
                  }
                />
                {((values.details.lastModifedDate && values.details.lastModifedDate!=="") || values.details.loanStatusId===loanStatuses.MODIFYING) && (
                  <SideLabelDateInput
                    label="Last Modified Date"
                    fieldName="details.lastModifiedDate"
                    placeholder="MM/DD/YYYY"
                    isDisabled={!isEditing}
                    shouldHideForQuote={isQuote}
                  />
                )}
                <SideLabelDateInput
                  label="Initial Maturity Date"
                  fieldName="details.maturityDate"
                  placeholder="MM/DD/YYYY"
                  isDisabled={!isEditing}
                  shouldHideForQuote={isQuote}
                />
                <SideLabelDateInput
                  label="Extended Maturity Date"
                  fieldName="details.extendedMaturityDate"
                  placeholder="MM/DD/YYYY"
                  isDisabled={!isEditing}
                  shouldHideForQuote={isQuote}
                />
                <SideLabelDateInput
                  label="I/O Ends"
                  fieldName="details.interestOnlyEndDate"
                  placeholder="Interest Only Ends"
                  isDisabled
                  shouldHideForQuote={isQuote}
                />
                <SideLabelIntegerInput label="Payment Days" fieldName="details.paymentDay" isDisabled={!isEditing} shouldHideForQuote={isQuote} />
                <SideLabelAutoCompleteInput
                  label="Rate Type"
                  id="dropdownForRateType"
                  fieldName="details.rateTypeId"
                  selectValue="value"
                  options={interestRateTypeList}
                  isDisabled={!isEditing}
                />
                {isMezz && (
                  <SideLabelAutoCompleteInput
                    label="Mezz Rate"
                    id="dropdownForMezzRate"
                    fieldName="details.mezzRateTypeId"
                    selectValue="value"
                    options={mezzRateTypeList}
                    isDisabled={!isEditing}
                    shouldHideForQuote={isQuote}
                  />
                )}
                {!isInternallyFixed && (
                  <SideLabelPercentInput
                    label="Loan Spread"
                    placeholder="Loan Spread"
                    fieldName="details.loanSpread"
                    isDisabled={!isEditing}
                    decimalScale={3}
                  />
                )}
                <SideLabelAutoCompleteInput
                  label="Index"
                  id="dropdownForIndex"
                  fieldName="details.indexId"
                  selectValue="value"
                  options={this.getIndexTypes()}
                  isDisabled={!isEditing}
                />
                {isFloating && 
                  <SideLabelTextAreaInput 
                    label="Fallback Rate" 
                    placeholder="Fallback Rate" 
                    fieldName="details.fallbackRate" 
                    isDisabled={!isEditing}
                    />
                }
                {/* {
                  <SideLabelCalculatedField
                    label="All-In Rate"
                    calculationFunc={fieldValues => num(fieldValues.details.allInRate / 100).format("0.000%")}
                    placeholder="All-In Rate"
                    isDisabled={!isEditing}
                    decimalScale={3}
                  />
                } */}
                {isFloating && (
                  <SideLabelTextAreaInput
                    label="Rate Modification"
                    placeholder="Rate Modification"
                    fieldName="details.spreadReductionTerms" //spreadReductionTerms renamed to rate modification
                    isDisabled={!isEditing}
                    shouldHideForQuote={isQuote}
                  />
                )}
                {isFixed && 
                  <SideLabelPercentInput 
                    label="Rate" 
                    placeholder="Rate" 
                    fieldName="details.interestRate" 
                    isDisabled={!isEditing || (isEditing && isQuote && !isInternallyFixed)} 
                    decimalScale={3} />
                }
                {isFixed && (
                  <SideLabelTextAreaInput
                    label="Rate Reduction Terms"
                    placeholder="Rate Reduction Terms"
                    fieldName="details.rateReductionTerms"
                    isDisabled={!isEditing}
                  />
                )}
                {isFloating && (
                  <SideLabelIntegerInput label="Lookback Days" fieldName="details.lookbackDays" isDisabled={!isEditing} shouldHideForQuote={isQuote} />
                )}

                <SideLabelAutoCompleteInput
                  id="dropdownForBusinessDayAdjustment"
                  label="Payment Day Adjustment"
                  fieldName="details.businessDayAdjustmentId"
                  selectValue="value"
                  options={businessDayAdjustmentList}
                  isDisabled={!isEditing}
                  shouldHideForQuote={isQuote}
                />
                {isFloating && (
                  <SideLabelAutoCompleteInput
                    label="Rounding"
                    fieldName="details.roundingId"
                    selectValue="value"
                    options={roundingList}
                    isDisabled={!isEditing}
                    shouldHideForQuote={isQuote}
                  />
                )}
                {isFloating && (
                  <SideLabelPercentInput
                    label="Index/Embedded Floor"
                    placeholder="Index/Embedded Floor"
                    fieldName="details.indexFloor"
                    isDisabled={!isEditing}
                    decimalScale={3}
                    allowNegative
                  />
                )}
                {isFloating && (
                  <SideLabelPercentInput
                    label="Embedded Cap"
                    placeholder="Embedded Cap"
                    fieldName="details.embeddedCap"
                    isDisabled={!isEditing}
                    decimalScale={3}
                    shouldHideForQuote={isQuote}
                  />
                )}

                <SideLabelAutoCompleteInput
                  id="dropdownForDaycount"
                  label="Daycount"
                  fieldName="details.daycountId"
                  selectValue="value"
                  options={daycountList}
                  isDisabled={!isEditing}
                />
                <SideLabelPercentInput
                  label="Default Interest Spread"
                  placeholder="Default Interest Spread"
                  fieldName="details.defaultInterestSpread"
                  isDisabled={!isEditing}
                  decimalScale={3}
                />
                <SideLabelTextAreaInput
                  label="Default Interest Condition"
                  fieldName="details.defaultInterestCondition"
                  placeholder="Default Interest Condition"
                  isDisabled={!isEditing}
                />
                <SideLabelTextAreaInput label="Recurring Costs" placeholder="Recurring Costs" fieldName="details.recurringCosts" isDisabled={!isEditing} />
                <SideLabelTextAreaInput
                  label="Conversion Options"
                  placeholder="Conversion Options"
                  fieldName="details.conversionOption"
                  isDisabled={!isEditing}
                />
                {this.isValueAddOrConstructionOrHudOrLineOfCredit(values) && (
                  <SideLabelTextAreaInput
                    label="Additional Interest"
                    placeholder="Additional Interest"
                    fieldName="details.additionalInterest"
                    isDisabled={!isEditing}
                  />
                )}
                <SideLabelTextInput label="Collateralization" placeholder="Collateralization" fieldName="details.collateralization" isDisabled={!isEditing} />
                {this.isPaidOff(values) && (
                  <>
                    <SideLabelDateInput
                      label="Paid Off Date"
                      placeholder="MM/DD/YYYY"
                      fieldName="details.paidOffDate"
                      isDisabled={!isEditing}
                      shouldHideForQuote={isQuote}
                    />
                    <SideLabelMoneyInput
                      label="Actual Prepay Penalty"
                      fieldName="details.actualPrepaymentPenalty"
                      placeholder="Actual Prepay Penalty"
                      isDisabled={!isEditing}
                      shouldHideForQuote={isQuote}
                    />
                    <SideLabelMoneyInput
                      label="Balance At Payoff"
                      fieldName="details.balanceAtPayoff"
                      placeholder="Balance At Payoff"
                      isDisabled={!isEditing}
                      shouldHideForQuote={isQuote}
                    />
                  </>
                )}
              </>
            )}
          </LoanDetailsGrid>
        </div>
      </>
    );
  }
}

const mapStateToProps = ({ loanDetails, authentication }) => ({
  authentication,
  ancillary: loanDetails.loanDetails.ancillary
});

export default compose(connect, reduxConnect(mapStateToProps, {}))(LoanDetailsChildSection);
